import { type Action, createHook, createStore } from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context';
import {
	logException,
	type SendAnalyticsEvent,
} from '@atlassian/conversation-assistant-instrumentation';
import { type FileUploadResponseItem } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { isTextFile } from '../../utils/fileTypeHelpers';
import { generateFilePreview } from '../../utils/generatePreview';

export type UploadedFile = FileUploadResponseItem & {
	isLoading: boolean;
	error?: string;
	fileObject?: File;
	previewBlobUrl?: string;
	isPreviewLoading?: boolean;
	previewError?: string;
};

type FileTypeAttachment = {
	type: 'file';
	data: UploadedFile;
};
type ExternalContextTypeAttachment = {
	type: 'external';
	data: PromptInputContext;
};
type AttachmentsPerKey = {
	attachments: Map<string, FileTypeAttachment | ExternalContextTypeAttachment>;
};
type State = {
	[key: string]: AttachmentsPerKey;
};

const defaultStateContextPerKey: AttachmentsPerKey = {
	attachments: new Map(),
};

/**
 * Cleanup blob URLs for file attachments to prevent memory leaks.
 * Only runs when the feature flag is enabled.
 */
const cleanupBlobUrls = (
	attachments: (FileTypeAttachment | ExternalContextTypeAttachment)[],
): void => {
	// Suppress exposure: This check fires during cleanup operations.
	if (
		!FeatureGates.getExperimentValue('rovo_chat_file_previews_experiment', 'isEnabled', false, {
			fireExperimentExposure: false,
		})
	) {
		return;
	}

	attachments.forEach((attachment) => {
		if (attachment.type === 'file' && attachment.data.previewBlobUrl) {
			URL.revokeObjectURL(attachment.data.previewBlobUrl);
		}
	});
};

const updateAttachements: (
	key: string,
	entries: [string, FileTypeAttachment | ExternalContextTypeAttachment][],
	overwrite?: boolean,
) => Action<State, any, any> = (
	key: string,
	entries: [string, FileTypeAttachment | ExternalContextTypeAttachment][],
	overwrite: boolean = false,
) => {
	return ({ setState, getState }) => {
		const state = getState()[key] || defaultStateContextPerKey;
		setState({
			[key]: {
				attachments: new Map([...(!overwrite ? state.attachments.entries() : []), ...entries]),
			},
		});
	};
};

const store = createStore({
	name: 'conversationAttachments',
	initialState: {} as State,
	actions: {
		addFiles:
			(key: string, files: UploadedFile[], sendAnalyticsEvent: SendAnalyticsEvent) =>
			({ getState: _getState, dispatch }) => {
				// Suppress exposure: This check fires when determining if preview generation is needed.
				const needsPreview = (file: UploadedFile) =>
					file.fileObject &&
					(file.fileObject.type.startsWith('image/') ||
						isTextFile(file.fileObject.type, file.fileObject.name)) &&
					FeatureGates.getExperimentValue(
						'rovo_chat_file_previews_experiment',
						'isEnabled',
						false,
						{
							fireExperimentExposure: false,
						},
					);

				// Add files with preview loading state if needed
				const fileAttachments: [string, FileTypeAttachment][] = files.map((file) => [
					file.name,
					{
						type: 'file',
						data: needsPreview(file) ? { ...file, isPreviewLoading: true } : file,
					},
				]);
				dispatch(updateAttachements(key, fileAttachments));

				files.forEach((file) => {
					if (needsPreview(file) && file.fileObject) {
						generateFilePreview(file.fileObject)
							.then((previewBlobUrl) => {
								// Check if file still exists and wasn't removed/errored during preview generation
								const currentState = _getState()[key] || defaultStateContextPerKey;
								const currentAttachment = currentState.attachments.get(file.name);

								if (
									!currentAttachment ||
									currentAttachment.type !== 'file' ||
									currentAttachment.data.error
								) {
									// File was removed or errored while preview was generating, cleanup the blob
									URL.revokeObjectURL(previewBlobUrl);
									return;
								}

								dispatch(
									store.actions.updateFileWithName(key, file.name, {
										previewBlobUrl,
										isPreviewLoading: false,
									}),
								);
							})
							.catch((error) => {
								// Log error to Sentry for monitoring
								logException({
									exception: error,
									name: 'file-preview-generation-error',
									tags: {
										fileType: file.fileObject?.type,
									},
								});

								sendAnalyticsEvent({
									action: 'failed',
									actionSubject: 'preview',
									actionSubjectId: 'thumbnailGeneration',
									attributes: {
										fileType: file.fileObject?.type || 'unknown',
									},
								});

								dispatch(
									store.actions.updateFileWithName(key, file.name, {
										isPreviewLoading: false,
										previewError: error instanceof Error ? error.message : String(error),
									}),
								);
							});
					}
				});
			},
		addExternalContext:
			(key: string, context: PromptInputContext[]) =>
			({ getState: _getState, dispatch }) => {
				const externalContextAttachments: [string, ExternalContextTypeAttachment][] = context.map(
					(context) => [
						context.key,
						{ type: 'external', data: context } as ExternalContextTypeAttachment,
					],
				);
				dispatch(updateAttachements(key, externalContextAttachments));
			},
		updateFileWithName:
			(key: string, fileName: string, fileData: Partial<UploadedFile>) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;
				const attachment = currentAttachtments.get(fileName);
				if (attachment && attachment.type === 'file') {
					dispatch(
						updateAttachements(key, [
							[
								fileName,
								{ ...attachment, data: { ...attachment.data, ...fileData } } as FileTypeAttachment,
							],
						]),
					);
				}
			},
		removeFile:
			(key: string, fileName: string) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup blob URL before removing
				const attachment = currentAttachtments.get(fileName);
				if (attachment) {
					cleanupBlobUrls([attachment]);
				}

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(
							([entryKey, value]) => value.type !== 'file' || entryKey !== fileName,
						),
						true,
					),
				);
			},
		removeExternalContext:
			(key: string, id: string) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(
							([entryKey, value]) => value.type !== 'external' || entryKey !== id,
						),
						true,
					),
				);
			},
		clearFiles:
			(key: string) =>
			({ dispatch, getState }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup all file blob URLs
				cleanupBlobUrls([...currentAttachtments.values()]);

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(([_, value]) => value.type !== 'file'),
						true,
					),
				);
			},
		clearAttachments:
			(key: string) =>
			({ dispatch, getState }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup all blob URLs
				cleanupBlobUrls([...currentAttachtments.values()]);

				dispatch(updateAttachements(key, [], true));
			},
	},
});

export const useAttachmentsStore = createHook(store);

export const useAllAttachmentsStore = createHook(store, {
	selector: (state, key: string) => [
		...(state[key]?.attachments?.values() || defaultStateContextPerKey.attachments.values()),
	],
});
export const useFileAttachmentsStore = createHook(store, {
	selector: (state, key: string) =>
		[...(state[key]?.attachments?.values() || [])]
			.filter((item) => item.type === 'file')
			.map((item) => item.data as UploadedFile) || [],
});
export const useExternalContextsStore = createHook(store, {
	selector: (state, key: string) =>
		[...(state[key]?.attachments?.values() || [])]
			?.filter((item) => item.type === 'external')
			?.map((item) => item.data as PromptInputContext) || [],
});
